"use client"

import type React from "react"

import { useState, useEffect, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import { MessageCircle, Send, User, Lock, AtSign, Bell, BellOff, Flag } from "lucide-react"
import { createClient } from "@/lib/supabase/client"
import { toast as sonnerToast } from "sonner"
import { useToast } from "@/hooks/use-toast"
import Link from "next/link"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { createNotificationWithPush } from "@/lib/utils/push-notifications"
import { filterProfanity } from "@/lib/utils/profanity-filter"
import { sendContentReportNotificationEmail } from "@/app/actions/send-email"

import { AuthWall } from "@/components/auth-wall"

interface ChatMessage {
  id: string
  user_id: string
  message: string
  created_at: string
  users: {
    name: string
    avatar_url: string | null
  }
  spot_chat_mentions?: {
    mentioned_user_id: string
    users: {
      name: string | null
      first_name: string | null
      last_name: string | null
      avatar_url: string | null
    }
  }[]
}

interface MentionUser {
  id: string
  name: string
  avatar_url: string | null
  first_name: string | null
  last_name: string | null
}

export function SpotChat({ spotId, user, onInputClick, isPlus }: { spotId: string; user: any; onInputClick?: () => void; isPlus?: boolean }) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [newMessage, setNewMessage] = useState("")
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const isInitialLoad = useRef(true)
  const { toast } = useToast()
  const supabase = createClient()
  
  // Mention autocomplete state
  const [mentionQuery, setMentionQuery] = useState("")
  const [mentionUsers, setMentionUsers] = useState<MentionUser[]>([])
  const [showMentionPopover, setShowMentionPopover] = useState(false)
  const [mentionPosition, setMentionPosition] = useState({ start: 0, end: 0 })
  const inputRef = useRef<HTMLInputElement>(null)
  
  // Follow chat state
  const [isFollowingChat, setIsFollowingChat] = useState(false)
  const [loadingFollow, setLoadingFollow] = useState(false)
  const [blockedUserIds, setBlockedUserIds] = useState<string[]>([])

  // Fetch blocked users
  const fetchBlockedUsers = useCallback(async () => {
    if (!currentUserId) return
    try {
      const { data, error } = await supabase
        .from("blocked_users")
        .select("blocked_id")
        .eq("blocker_id", currentUserId)
      
      if (error) throw error
      setBlockedUserIds(data?.map(b => b.blocked_id) || [])
    } catch (error) {
      console.error("Error fetching blocked users:", error)
    }
  }, [currentUserId, supabase])

  // Check if user is following this spot chat
  const checkFollowStatus = useCallback(async () => {
    if (!currentUserId) return
    
    try {
      const { data, error } = await supabase
        .from("spot_chat_follows")
        .select("id")
        .eq("user_id", currentUserId)
        .eq("surf_spot_id", spotId)
        .maybeSingle()
      
      if (error && error.code !== 'PGRST116') {
        console.error("Error checking follow status:", error)
        return
      }
      
      setIsFollowingChat(!!data)
    } catch (error) {
      console.error("Error checking follow status:", error)
    }
  }, [currentUserId, spotId, supabase])

  // Toggle follow chat
  const toggleFollowChat = async () => {
    if (!currentUserId || loadingFollow) return
    
    triggerHaptic('selection')
    setLoadingFollow(true)
    try {
      if (isFollowingChat) {
        // Unfollow - delete the follow record
        const { error } = await supabase
          .from("spot_chat_follows")
          .delete()
          .eq("user_id", currentUserId)
          .eq("surf_spot_id", spotId)
        
        if (error) throw error
        setIsFollowingChat(false)
        toast({
          title: "Unfollowed chat",
          description: "You won't receive notifications for new messages in this chat.",
        })
      } else {
        // Follow - insert the follow record
        const { error } = await supabase
          .from("spot_chat_follows")
          .insert({
            user_id: currentUserId,
            surf_spot_id: spotId,
          })
        
        if (error) throw error
        setIsFollowingChat(true)
        toast({
          title: "Following chat",
          description: "You'll receive notifications when new messages are posted.",
        })
      }
    } catch (error) {
      console.error("Error toggling follow:", error)
      toast({
        title: "Error",
        description: "Failed to update follow status. Please try again.",
        variant: "destructive",
      })
    } finally {
      setLoadingFollow(false)
    }
  }

  useEffect(() => {
    if (user) {
      // Fetch current user's users.id (primary key) on mount
      const fetchCurrentUserId = async () => {
        try {
          const {
            data: { user: authUser },
          } = await supabase.auth.getUser()
          if (authUser) {
            const { data: userProfile } = await supabase
              .from("users")
              .select("id, name, avatar_url")
              .eq("auth_id", authUser.id)
              .single()
            if (userProfile) {
              setCurrentUserId(userProfile.id)
            }
          }
        } catch (error) {
          console.error("Error fetching current user ID:", error)
        }
      }

      fetchCurrentUserId().then(() => {
        // Check follow status after currentUserId is set
        checkFollowStatus()
        fetchBlockedUsers()
      })
      fetchMessages()
      deleteOldMessages()

      const channel = supabase
        .channel(`spot-chat-${spotId}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "spot_chat_messages",
            filter: `surf_spot_id=eq.${spotId}`,
          },
          async (payload) => {
            // Fetch the complete message with user info
            const { data: newMessage } = await supabase
              .from("spot_chat_messages")
              .select(
                `
                id,
                user_id,
                message,
                created_at,
                users (
                  name,
                  avatar_url
                ),
                spot_chat_mentions (
                  mentioned_user_id,
                  users (
                    name,
                    avatar_url
                  )
                )
              `
              )
              .eq("id", payload.new.id)
              .single()

            if (newMessage) {
              setMessages((prev) => {
                // Check if message already exists (to prevent duplicates)
                const exists = prev.some((msg) => msg.id === newMessage.id)
                if (exists) {
                  // Message already exists, just return previous state
                  return prev
                }
                // Check if there's a temp message with same content that should be replaced
                const tempMessageIndex = prev.findIndex(
                  (msg) => msg.id.startsWith("temp-") && 
                           msg.message === newMessage.message &&
                           msg.user_id === newMessage.user_id
                )
                if (tempMessageIndex !== -1) {
                  // Replace optimistic message with real one
                  const updated = [...prev]
                  updated[tempMessageIndex] = newMessage as ChatMessage
                  return updated
                }
                // Add new message (from another user)
                return [...prev, newMessage as ChatMessage]
              })
              // Scroll to bottom after a short delay
              setTimeout(() => {
                scrollToBottom()
              }, 100)
            }
          },
        )
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "spot_chat_messages",
            filter: `surf_spot_id=eq.${spotId}`,
          },
          () => {
            fetchMessages()
          },
        )
        .subscribe()

      return () => {
        supabase.removeChannel(channel)
      }
    } else {
      const dummyMessages: ChatMessage[] = [
        {
          id: "1",
          user_id: "dummy1",
          message: "Conditions looking great for tomorrow morning!",
          created_at: new Date(Date.now() - 3600000).toISOString(),
          users: { name: "Alex K.", avatar_url: null },
        },
        {
          id: "2",
          user_id: "dummy2",
          message: "Anyone heading out around 7am?",
          created_at: new Date(Date.now() - 1800000).toISOString(),
          users: { name: "Jordan P.", avatar_url: null },
        },
        {
          id: "3",
          user_id: "dummy3",
          message: "Just got back, waves were clean! 🌊",
          created_at: new Date(Date.now() - 900000).toISOString(),
          users: { name: "Sam W.", avatar_url: null },
        },
        {
          id: "4",
          user_id: "dummy4",
          message: "Parking was full by 8am today, FYI",
          created_at: new Date(Date.now() - 300000).toISOString(),
          users: { name: "Taylor M.", avatar_url: null },
        },
      ]
      setMessages(dummyMessages)
      setLoading(false)
    }
  }, [spotId, user])

  useEffect(() => {
    if (messages.length > 0) {
      if (isInitialLoad.current) {
        // First time we get messages, scroll to bottom instantly
        scrollToBottom("instant")
        isInitialLoad.current = false
      } else {
        // Subsequent message updates (like new messages arriving), scroll smoothly
        scrollToBottom("smooth")
      }
    }
  }, [messages])

  // Scroll to bottom - ScrollArea will handle the scrolling container
  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    if (viewportRef.current) {
      const scrollHeight = viewportRef.current.scrollHeight
      viewportRef.current.scrollTo({ top: scrollHeight, behavior })
    }
  }

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from("spot_chat_messages")
        .select(
          `
          id,
          user_id,
          message,
          created_at,
          users (
            name,
            avatar_url
          ),
          spot_chat_mentions (
            mentioned_user_id,
            users (
              name,
              avatar_url
            )
          )
        `,
        )
        .eq("surf_spot_id", spotId)
        .order("created_at", { ascending: true })
        .limit(100)

      if (error) throw error
      setMessages(data || [])
    } catch (error) {
      console.error("Error fetching messages:", error)
    } finally {
      setLoading(false)
    }
  }

  const deleteOldMessages = async () => {
    try {
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      await supabase
        .from("spot_chat_messages")
        .delete()
        .eq("surf_spot_id", spotId)
        .lt("created_at", thirtyDaysAgo.toISOString())
    } catch (error) {
      console.error("Error deleting old messages:", error)
    }
  }

  // Search users for mention autocomplete
  const searchUsersForMention = useCallback(async (query: string) => {
    if (!query || query.trim().length === 0) {
      // If query is empty, show recent users or all users
      try {
        const { data, error } = await supabase
          .from("users")
          .select("id, name, first_name, last_name, avatar_url")
          .eq("is_active", true)
          .neq("status", "banned")
          .order("created_at", { ascending: false })
          .limit(10)

        if (error) {
          console.error("Error fetching users for mention:", error)
          setMentionUsers([])
          return
        }
        
        // Exclude current user if we have currentUserId
        const filtered = currentUserId 
          ? (data || []).filter((u) => u.id !== currentUserId)
          : (data || [])
        setMentionUsers(filtered)
      } catch (error) {
        console.error("Error fetching users for mention:", error)
        setMentionUsers([])
      }
      return
    }

    try {
      // Search by name, first_name, or last_name
      const { data, error } = await supabase
        .from("users")
        .select("id, name, first_name, last_name, avatar_url")
        .or(`name.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
        .eq("is_active", true)
        .neq("status", "banned")
        .limit(10)

      if (error) {
        console.error("Error searching users for mention:", error)
        setMentionUsers([])
        return
      }

      // Exclude current user if we have currentUserId
      const filtered = currentUserId 
        ? (data || []).filter((u) => u.id !== currentUserId)
        : (data || [])
      setMentionUsers(filtered)
    } catch (error) {
      console.error("Error searching users for mention:", error)
      setMentionUsers([])
    }
  }, [currentUserId, supabase])

  // Handle @ mention input
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setNewMessage(value)

    // Get cursor position immediately
    const cursorPosition = e.target.selectionStart || 0
    const textBeforeCursor = value.substring(0, cursorPosition)
    const lastAtIndex = textBeforeCursor.lastIndexOf("@")

    if (lastAtIndex !== -1) {
      // Check if @ is part of a word (not valid mention)
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : " "
      if (charBeforeAt.match(/\S/)) {
        // @ is part of a word, not a mention
        setShowMentionPopover(false)
        return
      }

      // Extract query after @
      const query = textBeforeCursor.substring(lastAtIndex + 1)
      const spaceIndex = query.indexOf(" ")
      
      if (spaceIndex === -1) {
        // No space found, this is a mention query
        setMentionQuery(query)
        setMentionPosition({ start: lastAtIndex, end: cursorPosition })
        setShowMentionPopover(true)
        // Trigger search immediately
        searchUsersForMention(query)
      } else {
        // Space found, mention query ended
        setShowMentionPopover(false)
      }
    } else {
      setShowMentionPopover(false)
    }
  }

  // Insert mention into message
  const insertMention = (user: MentionUser) => {
    const displayName = user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || "User"
    const beforeMention = newMessage.substring(0, mentionPosition.start)
    const afterMention = newMessage.substring(mentionPosition.end)
    const mentionText = `@${displayName} `
    
    setNewMessage(beforeMention + mentionText + afterMention)
    setShowMentionPopover(false)
    setMentionQuery("")
    
    // Focus input and set cursor position
    setTimeout(() => {
      if (inputRef.current) {
        const newPosition = beforeMention.length + mentionText.length
        inputRef.current.focus()
        inputRef.current.setSelectionRange(newPosition, newPosition)
      }
    }, 0)
  }

  // Parse mentions from message text
  const parseMentions = async (message: string): Promise<{ displayName: string; userId: string | null }[]> => {
    const mentionRegex = /@([^\s@]+)/g
    const mentions: { displayName: string; userId: string | null }[] = []
    let match

    while ((match = mentionRegex.exec(message)) !== null) {
      const displayName = match[1]
      mentions.push({
        displayName,
        userId: null, // Will be resolved later
      })
    }

    return mentions
  }

  // Render message with highlighted mentions
  const renderMessageWithMentions = (message: string, mentions?: ChatMessage['spot_chat_mentions'], isOwnMessage: boolean = false) => {
    // Always check for mentions in the message text, even if mentions array is empty
    const mentionRegex = /@([^\s@]+)/g
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match

    while ((match = mentionRegex.exec(message)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(message.substring(lastIndex, match.index))
      }

      const displayName = match[1]
      // Check if this mention matches a stored mention
      const mentionedUser = mentions?.find((m) => {
        const mentionName = m.users?.name || 
          `${m.users?.first_name || ""} ${m.users?.last_name || ""}`.trim()
        return mentionName === displayName || 
          m.users?.first_name === displayName ||
          m.users?.last_name === displayName
      })
      
      if (mentionedUser) {
        parts.push(
          <span key={match.index} className={`font-semibold ${isOwnMessage ? 'text-blue-100' : 'text-blue-600 dark:text-blue-400'}`}>
            @{displayName}
          </span>
        )
      } else {
        // Still highlight even if not in mentions array (for display purposes)
        parts.push(
          <span key={match.index} className={`font-semibold ${isOwnMessage ? 'text-blue-100' : 'text-blue-600 dark:text-blue-400'}`}>
            @{displayName}
          </span>
        )
      }

      lastIndex = match.index + match[0].length
    }

    // Add remaining text
    if (lastIndex < message.length) {
      parts.push(message.substring(lastIndex))
    }

    // If no mentions found, return plain text
    if (parts.length === 0) {
      return <p className="text-sm">{message}</p>
    }

    return <p className="text-sm">{parts}</p>
  }

  // Create notifications for mentioned users
  const createMentionNotifications = async (messageId: string, mentionedUserIds: string[], spotName?: string) => {
    if (mentionedUserIds.length === 0) return

    try {
      // Get spot name for notification
      let spotNameForNotification = spotName || "this spot"
      if (!spotName) {
        const { data: spot } = await supabase
          .from("surf_spots")
          .select("name")
          .eq("id", spotId)
          .single()
        if (spot) {
          spotNameForNotification = spot.name
        }
      }

      // Get current user's name for notification
      const { data: currentUser } = await supabase
        .from("users")
        .select("name, first_name, last_name")
        .eq("id", currentUserId)
        .single()

      const senderName = currentUser?.name || 
        `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim() || 
        "Someone"

      // Create notifications for each mentioned user
      const notifications = mentionedUserIds.map((mentionedUserId) => ({
        user_id: mentionedUserId,
        type: "mention",
        title: "You were mentioned",
        message: `${senderName} mentioned you in a discussion about ${spotNameForNotification}`,
        action_url: `/spot/${spotId}`,
        related_id: messageId,
      }))

      const { error } = await supabase.from("notifications").insert(notifications)
      if (error) {
        console.error("Error creating mention notifications:", error)
      }
    } catch (error) {
      console.error("Error creating mention notifications:", error)
    }
  }

  // Create notifications for users following this spot chat
  const createFollowNotifications = async (messageId: string, spotName?: string) => {
    if (!currentUserId) return

    try {
      // Get all users following this spot chat (excluding the sender)
      const { data: followers, error: followersError } = await supabase
        .from("spot_chat_follows")
        .select("user_id")
        .eq("surf_spot_id", spotId)
        .neq("user_id", currentUserId) // Don't notify the sender

      if (followersError) {
        console.error("Error fetching chat followers:", followersError)
        return
      }

      if (!followers || followers.length === 0) return

      // Get spot name for notification
      let spotNameForNotification = spotName || "this spot"
      if (!spotName) {
        const { data: spot } = await supabase
          .from("surf_spots")
          .select("name")
          .eq("id", spotId)
          .single()
        if (spot) {
          spotNameForNotification = spot.name
        }
      }

      // Get current user's name for notification
      const { data: currentUser } = await supabase
        .from("users")
        .select("name, first_name, last_name")
        .eq("id", currentUserId)
        .single()

      const senderName = currentUser?.name || 
        `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim() || 
        "Someone"

      // Create notifications for each follower
      for (const follower of followers) {
        await createNotificationWithPush(supabase, {
          user_id: follower.user_id,
          type: "spot_chat",
          title: "New message in spot chat",
          message: `${senderName} posted a new message in the ${spotNameForNotification} discussion`,
          action_url: `/spot/${spotId}`,
          related_id: messageId,
        })
      }
    } catch (error) {
      console.error("Error creating follow notifications:", error)
    }
  }

  const handleReportMessage = async (messageId: string, messageText: string) => {
    if (!currentUserId) {
      sonnerToast.error("You must be logged in to report content")
      return
    }

    triggerHaptic('impact')
    
    try {
      const { error } = await supabase
        .from("content_reports")
        .insert({
          reporter_id: currentUserId,
          content_type: "message",
          content_id: messageId,
          reason: "User reported message",
          details: messageText,
          status: "pending"
        })

      if (error) throw error
      
      // Send email notification to admin
      const { data: reporterData } = await supabase
        .from("users")
        .select("name, email")
        .eq("id", currentUserId)
        .single()

      await sendContentReportNotificationEmail(
        reporterData?.name || reporterData?.email || "Anonymous User",
        "message",
        messageId,
        "User reported message",
        messageText
      )
      
      sonnerToast.success("Message reported. Our moderators will look into it.")
    } catch (error) {
      console.error("Error reporting message:", error)
      sonnerToast.error("Failed to submit report. Please try again.")
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newMessage.trim() || !user || !currentUserId) return

    // Apply profanity filter
    const messageContent = filterProfanity(newMessage.trim())
    setNewMessage("") // Clear input immediately for better UX

    setSending(true)
    try {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser()
      if (!authUser) throw new Error("Not authenticated")

      // Fetch the user's profile to get the users.id (primary key) from auth_id
      const { data: userProfile, error: userError } = await supabase
        .from("users")
        .select("id, name, avatar_url")
        .eq("auth_id", authUser.id)
        .single()

      if (userError || !userProfile) {
        console.error("Error fetching user profile:", userError)
        setNewMessage(messageContent) // Restore message on error
        throw new Error("Could not find your user profile")
      }

      // Optimistically add message to state immediately
      const optimisticMessage: ChatMessage = {
        id: `temp-${Date.now()}`, // Temporary ID
        user_id: userProfile.id,
        message: messageContent,
        created_at: new Date().toISOString(),
        users: {
          name: userProfile.name || "You",
          avatar_url: userProfile.avatar_url || null,
        },
      }
      setMessages((prev) => [...prev, optimisticMessage])
      
      // Scroll to bottom immediately
      setTimeout(() => {
        scrollToBottom()
      }, 50)

      // Parse mentions from message
      const parsedMentions = await parseMentions(messageContent)
      
      // Search for users by display name to get their IDs
      const mentionedUserIds: string[] = []
      for (const mention of parsedMentions) {
        if (mention.userId) {
          mentionedUserIds.push(mention.userId)
        } else {
          // Sanitize display name to prevent SQL injection
          // Remove any characters that could break the query (%, _, special chars)
          // Supabase client should handle this, but we sanitize for extra safety
          const sanitizedDisplayName = mention.displayName
            .replace(/[%_\\]/g, '') // Remove SQL wildcards and backslashes
            .trim()
            .substring(0, 100) // Limit length to prevent DoS
          
          // Skip if display name is empty after sanitization
          if (!sanitizedDisplayName || sanitizedDisplayName.length === 0) {
            continue
          }

          // Try to find user by display name
          // Using Supabase's parameterized queries - the client handles escaping
          const { data: foundUser } = await supabase
            .from("users")
            .select("id")
            .or(`name.ilike.%${sanitizedDisplayName}%,first_name.ilike.%${sanitizedDisplayName}%,last_name.ilike.%${sanitizedDisplayName}%`)
            .eq("is_active", true)
            .limit(1)
            .maybeSingle()
          
          if (foundUser && foundUser.id !== currentUserId) {
            mentionedUserIds.push(foundUser.id)
          }
        }
      }

      // Insert message to database
      const { data: insertedMessage, error } = await supabase
        .from("spot_chat_messages")
        .insert({
          surf_spot_id: spotId,
          user_id: userProfile.id, // Use users.id (primary key), not auth_id
          message: messageContent,
        })
        .select(
          `
          id,
          user_id,
          message,
          created_at,
          users (
            name,
            avatar_url
          ),
          spot_chat_mentions (
            mentioned_user_id,
            users (
              name,
              first_name,
              last_name,
              avatar_url
            )
          )
        `
        )
        .single()

      if (error) {
        console.error("Error sending message:", error)
        // Remove optimistic message on error
        setMessages((prev) => prev.filter((msg) => msg.id !== optimisticMessage.id))
        setNewMessage(messageContent) // Restore message on error
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        })
      } else if (insertedMessage) {
        // Get spot name for notifications
        const { data: spot } = await supabase
          .from("surf_spots")
          .select("name")
          .eq("id", spotId)
          .single()
        const spotName = spot?.name

        // Store mentions if any
        if (mentionedUserIds.length > 0 && insertedMessage.id) {
          const mentionsToInsert = mentionedUserIds.map((userId) => ({
            message_id: insertedMessage.id,
            mentioned_user_id: userId,
          }))

          await supabase.from("spot_chat_mentions").insert(mentionsToInsert)
          
          // Create notifications for mentioned users
          await createMentionNotifications(insertedMessage.id, mentionedUserIds, spotName)
        }

        // Create notifications for users following this spot chat
        await createFollowNotifications(insertedMessage.id, spotName)

        // Replace optimistic message with real message from database
        setMessages((prev) =>
          prev.map((msg) => (msg.id === optimisticMessage.id ? (insertedMessage as ChatMessage) : msg))
        )
      }
    } catch (error) {
      console.error("Unexpected error sending message:", error)
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((msg) => msg.id.startsWith("temp-")))
      setNewMessage(messageContent) // Restore message on error
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      })
    } finally {
      setSending(false)
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 24) {
      return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    } else if (diffInHours < 48) {
      return "Yesterday"
    } else {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    }
  }

  const formatDateSeparator = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (messageDate.getTime() === today.getTime()) {
      return "Today"
    } else if (messageDate.getTime() === yesterday.getTime()) {
      return "Yesterday"
    } else {
      return date.toLocaleDateString("en-US", { month: "long", day: "numeric" })
    }
  }

  const isNewDay = (currentTimestamp: string, previousTimestamp: string | null) => {
    if (!previousTimestamp) return true
    const currentDate = new Date(currentTimestamp)
    const previousDate = new Date(previousTimestamp)
    return (
      currentDate.getDate() !== previousDate.getDate() ||
      currentDate.getMonth() !== previousDate.getMonth() ||
      currentDate.getFullYear() !== previousDate.getFullYear()
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <MessageCircle className="w-8 h-8 animate-pulse text-blue-500 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading chat...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Follow chat toggle - shown only when user is logged in */}
      {user && currentUserId && (
        <div className="flex items-center justify-between p-4 px-6 border-b border-slate-50 dark:border-slate-800/50 bg-white/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-2">
            <span className="text-lg">🔔</span>
            <Label htmlFor="follow-chat" className="text-sm font-bold text-slate-900 dark:text-white cursor-pointer">
              Follow chat for notifications
            </Label>
          </div>
          <Switch
            id="follow-chat"
            className="data-[state=checked]:bg-blue-600"
            checked={isFollowingChat}
            onCheckedChange={toggleFollowChat}
            disabled={loadingFollow}
          />
        </div>
      )}
      
      {/* Chat container - fixed height with scrollable messages area, showing last message at bottom */}
      <div className={`flex flex-col ${user && currentUserId ? 'h-[468px]' : 'h-[500px]'} overflow-hidden ${(!user || !isPlus) ? "blur-md pointer-events-none" : ""}`}>
        {/* Messages area - scrollable with fixed dimensions, showing last message at bottom */}
        <ScrollArea ref={viewportRef} className="flex-1 min-h-0">
          <div className="p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-6">
                  <MessageCircle className="w-10 h-10 text-slate-300 dark:text-slate-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No messages yet</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-[240px]">Start the conversation about this spot!</p>
              </div>
            ) : (
              messages
                .filter(msg => !blockedUserIds.includes(msg.user_id))
                .map((msg, index) => {
                const isOwnMessage = currentUserId && msg.user_id === currentUserId
                const previousMessage = index > 0 ? messages[index - 1] : null
                const showDateSeparator = isNewDay(msg.created_at, previousMessage?.created_at || null)

                return (
                  <div key={msg.id}>
                    {showDateSeparator && (
                      <div className="flex items-center gap-4 my-4">
                        <div className="flex-1 border-t border-border"></div>
                        <span className="text-xs text-muted-foreground font-medium">
                          {formatDateSeparator(msg.created_at)}
                        </span>
                        <div className="flex-1 border-t border-border"></div>
                      </div>
                    )}
                    <div
                      className={`flex gap-3 ${isOwnMessage ? "justify-end" : "justify-start"}`}
                    >
                      {!isOwnMessage && (
                        <Avatar className="w-8 h-8 flex-shrink-0">
                          <AvatarImage src={msg.users.avatar_url || "/placeholder.svg"} />
                          <AvatarFallback>{msg.users.name?.charAt(0) || "U"}</AvatarFallback>
                        </Avatar>
                      )}
                      <div className={`flex flex-col ${isOwnMessage ? "items-end max-w-[70%]" : "items-start max-w-[70%]"}`}>
                        {!isOwnMessage && (
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{msg.users.name || "Anonymous"}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatTime(msg.created_at)}
                            </span>
                          </div>
                        )}
                        <div
                          className={`rounded-lg px-3 py-2 ${
                            isOwnMessage ? "bg-blue-500 text-white" : "bg-muted"
                          }`}
                        >
                          {renderMessageWithMentions(msg.message, msg.spot_chat_mentions, isOwnMessage)}
                        </div>
                        {!isOwnMessage && (
                          <button 
                            onClick={() => handleReportMessage(msg.id, msg.message)}
                            className="flex items-center gap-1 text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1 hover:text-red-500 transition-colors"
                          >
                            <Flag className="w-2 h-2" />
                            Report
                          </button>
                        )}
                        {isOwnMessage && (
                          <span className="text-xs text-muted-foreground mt-1">
                            {formatTime(msg.created_at)}
                          </span>
                        )}
                      </div>
                      {isOwnMessage && (
                        <Avatar className="w-8 h-8 flex-shrink-0">
                          <AvatarImage src={msg.users.avatar_url || "/placeholder.svg"} />
                          <AvatarFallback>{msg.users.name?.charAt(0) || "U"}</AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  </div>
                )
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input area - fixed at bottom */}
        <div className="border-t border-slate-50 dark:border-slate-800/50 p-4 px-6 bg-white/50 dark:bg-slate-900/50 flex-shrink-0">
          <form onSubmit={handleSendMessage} className="flex gap-2 relative">
            <div className="flex-1 relative">
              <Input
                ref={inputRef}
                value={newMessage}
                onChange={handleMessageChange}
                onKeyDown={(e) => {
                  // Close mention popover on Escape
                  if (e.key === "Escape") {
                    setShowMentionPopover(false)
                  }
                }}
                className="rounded-xl border-slate-100 dark:border-slate-800 h-12 bg-white dark:bg-slate-900 shadow-sm"
                placeholder="Type a message about this spot..."
                disabled={sending}
                maxLength={500}
              />
              {showMentionPopover && (
                <div 
                  className="absolute bottom-full left-0 mb-2 w-[300px] bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-xl z-50 max-h-[200px] overflow-y-auto"
                  onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                >
                  <div className="p-2">
                    {mentionUsers.length === 0 ? (
                      <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-widest">Searching users...</div>
                    ) : (
                      mentionUsers.map((user) => {
                        const displayName = user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || "User"
                        return (
                          <div
                            key={user.id}
                            onClick={() => insertMention(user)}
                            onMouseDown={(e) => {
                              e.preventDefault()
                              insertMention(user)
                            }}
                            className="flex items-center gap-3 cursor-pointer px-3 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                          >
                            <Avatar className="w-8 h-8 border border-slate-100 dark:border-slate-800">
                              <AvatarImage src={user.avatar_url || undefined} />
                              <AvatarFallback className="text-xs bg-blue-50 text-blue-600 font-bold">
                                {displayName.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{displayName}</span>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
            <Button 
              type="submit" 
              disabled={sending || !newMessage.trim()} 
              size="icon"
              className="h-12 w-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20 active:scale-95 transition-all"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-3 text-center">
            Messages older than 30 days are automatically deleted.
          </p>
        </div>
      </div>

      {!isPlus && (
        <div className="absolute inset-0 flex items-center justify-center p-6 z-10">
          <Card className="w-full max-w-sm bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm shadow-xl border-slate-200 dark:border-slate-800 rounded-[2.5rem] p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto">
              <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="space-y-2">
              <p className="text-lg font-black uppercase tracking-tight text-slate-900 dark:text-slate-100">Join the Discussion</p>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400 max-w-[240px] leading-relaxed mx-auto">
                {user ? "Upgrade to Plus to share conditions and chat with other surfers at this spot." : "Sign in to share conditions and chat with other surfers at this spot."}
              </p>
            </div>
            <Button 
              onClick={onInputClick}
              className="h-12 w-full bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold shadow-lg shadow-blue-600/20 active:scale-95 transition-all"
            >
              {user ? "Upgrade to Plus" : "Join Community"}
            </Button>
          </Card>
        </div>
      )}
    </div>
  )
}